import { useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { AuthFrame } from '../components/AuthFrame';
import { Wordmark } from '../components/Wordmark';
import { Rejection } from '../components/Rejection';
import { ApiError } from '../net/client';
import { bannerFor, type Refusal } from '../session/refusals';
import { useSession } from '../session/useSession';
import { joinPath } from '../routes';
import { OTP_LENGTH, normaliseOtpInput } from '../session/otpCode';
import { countdown, useOtpStep } from '../session/useOtpStep';
import {
  RESET_CODE_LENGTH,
  RESET_CODE_SHAPE,
  resetCodeFault,
  type ResetCodeFault,
} from '../session/resetCode';
import { borderFor, fieldDetails, input, primary, stepForm } from './signInForm';
import { colors, fonts, type, verdict } from '../theme';

/**
 * Sign-in, unit G12. **No portal board draws it**: this is the mobile app's
 * Welcome and OtpEntry carried at their drawn 390 width, D20, with the pitch
 * and sign-up dropped by the user's ruling. Values are from
 * `veriplus-mobile-app/design/Welcome.dc.html` and `OtpEntry.dc.html`, the
 * behaviour from the built `WelcomeScreen.tsx` and `OtpEntryScreen.tsx`.
 *
 * The address and password live here, above both steps, so Back from the code
 * returns to them as typed. A signed-in answer needs no navigation: the
 * session's state changes and `App` renders the shell in place of this.
 */
export function SignIn() {
  const session = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'welcome' });

  switch (step.kind) {
    case 'code':
      return (
        <OtpEntry
          email={email.trim()}
          onBack={() => setStep({ kind: 'welcome' })}
          submit={(code) => session.verifyOtp(step.challengeId, code)}
          resend={() => session.resendOtp(step.challengeId)}
        />
      );
    case 'forgot':
      return (
        <ForgotPassword
          email={email}
          onEmail={setEmail}
          onBack={() => setStep({ kind: 'welcome' })}
          onSent={() => setStep({ kind: 'reset' })}
        />
      );
    case 'reset':
      return (
        <ResetPassword
          email={email.trim()}
          onBack={() => setStep({ kind: 'forgot' })}
          onDone={() => {
            setPassword('');
            setStep({ kind: 'welcome', notice: passwordChanged });
          }}
        />
      );
    case 'welcome':
      return (
        <Welcome
          email={email}
          password={password}
          notice={step.notice}
          onEmail={setEmail}
          onPassword={setPassword}
          onChallenge={(challengeId) => setStep({ kind: 'code', challengeId })}
          onForgot={() => setStep({ kind: 'forgot' })}
        />
      );
  }
}

/**
 * Where sign-in stands. Forgot password is the user's ruling on X5: a link
 * under the password, then the email, then the code and a new password, then
 * back here with the notice. Every word past the three ruled labels is D28.
 */
type Step =
  | { kind: 'welcome'; notice?: string }
  | { kind: 'code'; challengeId: string }
  | { kind: 'forgot' }
  | { kind: 'reset' };

/** D28, invented: no source has a reset flow to take it from. */
const passwordChanged = 'Your password has been changed. Sign in with the new one.';

type Field = 'email' | 'password';

/** Permissive on purpose, as mobile's: only the server knows whether an address exists. */
export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Welcome({
  email,
  password,
  notice,
  onEmail,
  onPassword,
  onChallenge,
  onForgot,
}: {
  email: string;
  password: string;
  notice: string | undefined;
  onEmail: (value: string) => void;
  onPassword: (value: string) => void;
  onChallenge: (challengeId: string) => void;
  onForgot: () => void;
}) {
  const session = useSession();
  const navigate = useNavigate();
  const [refusals, setRefusals] = useState<Partial<Record<Field, Refusal>>>({});
  const [banner, setBanner] = useState<Refusal | null>(null);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);

  const clear = (field: Field) =>
    setRefusals((r) => (r[field] === undefined ? r : { ...r, [field]: undefined }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    const found: Partial<Record<Field, Refusal>> = {};
    if (!EMAIL.test(email.trim()))
      found.email = { message: 'That does not look like an email address.' };
    // No length rule: an existing password is the server's to judge, mobile's reasoning.
    if (password.length === 0) found.password = { message: 'Enter your password.' };
    setBanner(null);
    setRefusals(found);
    if (Object.keys(found).length > 0) return;

    inFlight.current = true;
    setSending(true);
    try {
      const result = await session.signIn(email.trim(), password);
      if (result.kind === 'challenge') onChallenge(result.challengeId);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'invalid_credentials') {
        // One message for both: the API answers the same code for an unknown address.
        setRefusals({
          password: {
            message: 'That email and password do not match.',
            hint: 'Check both and try again.',
          },
        });
      } else if (fieldDetails(error, ['email', 'password'])) {
        setRefusals(fieldDetails(error, ['email', 'password'])!);
      } else {
        setBanner(bannerFor(error));
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  return (
    <AuthFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <StepHeading title="Sign in">
          Welcome back. Manage your products, codes and what consumers report.
        </StepHeading>
        <form
          noValidate
          onSubmit={submit}
          style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          {banner !== null ? (
            <Rejection>
              <RefusalWords refusal={banner} />
            </Rejection>
          ) : notice !== undefined ? (
            <Notice>{notice}</Notice>
          ) : null}

          <FieldBox id="signin-email" label="Email" refusal={refusals.email}>
            <input
              className="vp-box"
              id="signin-email"
              type="email"
              value={email}
              onChange={(e) => {
                onEmail(e.target.value);
                clear('email');
              }}
              placeholder="amina@example.com"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              disabled={sending}
              aria-invalid={refusals.email !== undefined}
              aria-describedby={refusals.email !== undefined ? 'signin-email-refusal' : undefined}
              style={{ ...input, borderColor: borderFor(refusals.email) }}
            />
          </FieldBox>

          <FieldBox id="signin-password" label="Password" refusal={refusals.password}>
            <PasswordBox
              id="signin-password"
              value={password}
              onChange={(value) => {
                onPassword(value);
                clear('password');
              }}
              autoComplete="current-password"
              disabled={sending}
              refusal={refusals.password}
            />
          </FieldBox>

          <button
            className="vp-link"
            type="button"
            onClick={onForgot}
            disabled={sending}
            style={{ ...linkButton, alignSelf: 'flex-end' }}
          >
            Forgot password?
          </button>

          <button
            className="vp-fill"
            type="submit"
            disabled={sending}
            aria-busy={sending}
            style={primary(!sending)}
          >
            Continue
          </button>

          <OtherWay
            question="Have an invitation code?"
            action="Join your brand"
            onClick={() => navigate(joinPath)}
            disabled={sending}
          />
        </form>
      </div>
    </AuthFrame>
  );
}

/**
 * A question and the other way in, L1, at the size and colour of OtpEntry's
 * "Didn't get it? Resend" line, the nearest drawn thing.
 */
export function OtherWay({
  question,
  action,
  onClick,
  disabled,
}: {
  question: string;
  action: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      style={{
        fontFamily: fonts.text,
        fontSize: '14px',
        color: colors.mutedInk,
        textAlign: 'center',
      }}
    >
      {question}{' '}
      <button
        className="vp-fill"
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={resendButton}
      >
        {action}
      </button>
    </div>
  );
}

/**
 * The six-cell code step. Sign-in spends the code for a session; joining, L1,
 * spends one for an account and one more to accept the invitation, so what a
 * code is spent on is the caller's. A refused code reads as not right, and any
 * other failure goes in the rejection box.
 */
export function OtpEntry({
  email,
  title = 'Enter your code',
  lead = 'We sent a 6-character code to',
  onBack,
  submit,
  resend: send,
}: {
  email: string;
  title?: string;
  lead?: string;
  onBack: () => void;
  submit: (code: string) => Promise<void>;
  resend: () => Promise<void>;
}) {
  const { code, setCode, verifying, refusal, secondsLeft, canVerify, verify, resend } = useOtpStep(
    submit,
    send,
  );

  return (
    <AuthFrame>
      <form noValidate onSubmit={verify} style={stepForm}>
        <BackButton onBack={onBack} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h1 style={{ ...wordmark, fontSize: '28px', letterSpacing: '-0.4px', margin: 0 }}>
            {title}
          </h1>
          <p
            style={{
              fontFamily: fonts.text,
              fontSize: '15px',
              lineHeight: 1.5,
              color: colors.mutedInk,
              margin: 0,
            }}
          >
            {lead} <span style={{ fontWeight: 600, color: colors.navy }}>{email}</span>
          </p>
        </div>

        <OtpCells code={code} onCode={setCode} disabled={verifying} />

        {refusal !== null ? (
          <Rejection>
            <RefusalWords refusal={refusal} />
          </Rejection>
        ) : null}

        <div
          style={{
            fontFamily: fonts.text,
            fontSize: '14px',
            color: colors.mutedInk,
            textAlign: 'center',
          }}
        >
          Didn&apos;t get it?{' '}
          {/* No disabled look is drawn: early, Resend keeps its colour and does nothing, as mobile's. */}
          <button
            className="vp-fill"
            type="button"
            onClick={resend}
            aria-disabled={secondsLeft > 0}
            aria-label={
              secondsLeft > 0 ? `Resend, available in ${countdown(secondsLeft)}` : 'Resend the code'
            }
            style={resendButton}
          >
            Resend
          </button>
          {secondsLeft > 0 ? ` available in ${countdown(secondsLeft)}` : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            className="vp-fill"
            type="submit"
            disabled={!canVerify}
            aria-busy={verifying}
            style={primary(canVerify)}
          >
            Verify
          </button>
          <div
            style={{
              fontFamily: fonts.text,
              fontSize: '13px',
              color: colors.mutedInk,
              textAlign: 'center',
            }}
          >
            The code expires in 10 minutes.
          </div>
        </div>
      </form>
    </AuthFrame>
  );
}

/** Six drawn cells behind one input, so paste and one-time-code autofill work unchanged. */
export function OtpCells({
  code,
  onCode,
  disabled,
}: {
  code: string;
  onCode: (code: string) => void;
  disabled: boolean;
}) {
  const [focused, setFocused] = useState(false);
  // The brand ring is on the last character typed, as the board draws it on M.
  const ringIndex = focused ? Math.max(0, code.length - 1) : -1;
  return (
    <div style={{ position: 'relative' }}>
      <div aria-hidden="true" style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        {Array.from({ length: OTP_LENGTH }, (_, i) => {
          const char = code[i];
          const border =
            i === ringIndex ? colors.brand : char !== undefined ? colors.navy : colors.line;
          return (
            <div key={i} data-cell={i} style={{ ...cell, border: `2px solid ${border}` }}>
              {char ?? ''}
            </div>
          );
        })}
      </div>
      <input
        value={code}
        onChange={(e) => onCode(normaliseOtpInput(e.target.value))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label="Verification code"
        autoComplete="one-time-code"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        autoFocus
        disabled={disabled}
        style={hiddenInput}
      />
    </div>
  );
}

/**
 * Forgot password, the first step: the address, and the API mails a code to
 * it. The API answers 204 for an address with no account too, so a sent code
 * is all this step can say.
 */
function ForgotPassword({
  email,
  onEmail,
  onBack,
  onSent,
}: {
  email: string;
  onEmail: (value: string) => void;
  onBack: () => void;
  onSent: () => void;
}) {
  const session = useSession();
  const [refusal, setRefusal] = useState<Refusal | undefined>(undefined);
  const [banner, setBanner] = useState<Refusal | null>(null);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    setBanner(null);
    if (!EMAIL.test(email.trim())) {
      setRefusal({ message: 'That does not look like an email address.' });
      return;
    }
    setRefusal(undefined);
    inFlight.current = true;
    setSending(true);
    try {
      await session.forgotPassword(email.trim());
      onSent();
    } catch (error) {
      const field = fieldDetails(error, ['email']);
      if (field?.email) setRefusal(field.email);
      else setBanner(bannerFor(error));
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  return (
    <AuthFrame>
      <form noValidate onSubmit={submit} style={stepForm}>
        <BackButton onBack={onBack} disabled={sending} />
        <StepHeading title="Reset your password">
          Enter the email you sign in with and we will send you a code.
        </StepHeading>

        {banner !== null ? (
          <Rejection>
            <RefusalWords refusal={banner} />
          </Rejection>
        ) : null}

        <FieldBox id="forgot-email" label="Email" refusal={refusal}>
          <input
            className="vp-box"
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => {
              onEmail(e.target.value);
              setRefusal(undefined);
            }}
            placeholder="amina@example.com"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            disabled={sending}
            aria-invalid={refusal !== undefined}
            aria-describedby={refusal !== undefined ? 'forgot-email-refusal' : undefined}
            style={{ ...input, borderColor: borderFor(refusal) }}
          />
        </FieldBox>

        <button
          className="vp-fill"
          type="submit"
          disabled={sending}
          aria-busy={sending}
          style={primary(!sending)}
        >
          Send code
        </button>
      </form>
    </AuthFrame>
  );
}

/** The backend's password rule, `auth.validation.ts`: 8 to 128 characters, nothing else. */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

type ResetField = 'code' | 'password';

/**
 * Forgot password, the second step: the mailed code and a new password. The
 * code is sent as typed, trimmed: the API folds its hyphens, case and
 * confusable characters itself (`normalizeTypedToken`), so nothing here
 * refuses one of those. A code that cannot be real, too short, too long, a
 * stray character or a failed check symbol, is refused here in its own
 * words, D29: the API would refuse it in the same words as a spent one. On success every session of the user is revoked, and
 * the user signs in again with the new password.
 */
function ResetPassword({
  email,
  onBack,
  onDone,
}: {
  email: string;
  onBack: () => void;
  onDone: () => void;
}) {
  const session = useSession();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [refusals, setRefusals] = useState<Partial<Record<ResetField, Refusal>>>({});
  const [banner, setBanner] = useState<Refusal | null>(null);
  const [sending, setSending] = useState(false);
  const inFlight = useRef(false);

  const clear = (field: ResetField) =>
    setRefusals((r) => (r[field] === undefined ? r : { ...r, [field]: undefined }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    const found: Partial<Record<ResetField, Refusal>> = {};
    const fault = resetCodeFault(code);
    if (fault !== null) found.code = refusalForCode(fault);
    // The backend's own words for its rule.
    if (password.length < PASSWORD_MIN)
      found.password = { message: 'Password must be at least 8 characters.' };
    else if (password.length > PASSWORD_MAX)
      found.password = { message: 'Password must be at most 128 characters.' };
    setBanner(null);
    setRefusals(found);
    if (Object.keys(found).length > 0) return;

    inFlight.current = true;
    setSending(true);
    try {
      await session.resetPassword(code, password);
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'invalid_reset_token') {
        setRefusals({ code: wrongResetCode });
      } else if (fieldDetails(error, { code: 'token', password: 'password' })) {
        setRefusals(fieldDetails(error, { code: 'token', password: 'password' })!);
      } else {
        setBanner(bannerFor(error));
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  return (
    <AuthFrame>
      <form noValidate onSubmit={submit} style={stepForm}>
        <BackButton onBack={onBack} disabled={sending} />
        <StepHeading title="Enter your code">
          If <span style={{ fontWeight: 600, color: colors.navy }}>{email}</span> has an account, we
          sent a code to it.
        </StepHeading>

        {banner !== null ? (
          <Rejection>
            <RefusalWords refusal={banner} />
          </Rejection>
        ) : null}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FieldBox id="reset-code" label="Code" refusal={refusals.code}>
            <input
              className="vp-box"
              id="reset-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                clear('code');
              }}
              placeholder={RESET_CODE_SHAPE}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              disabled={sending}
              aria-invalid={refusals.code !== undefined}
              aria-describedby={refusals.code !== undefined ? 'reset-code-refusal' : undefined}
              style={{ ...input, borderColor: borderFor(refusals.code) }}
            />
          </FieldBox>

          <FieldBox id="reset-password" label="New password" refusal={refusals.password}>
            <PasswordBox
              id="reset-password"
              value={password}
              onChange={(value) => {
                setPassword(value);
                clear('password');
              }}
              autoComplete="new-password"
              disabled={sending}
              refusal={refusals.password}
            />
          </FieldBox>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            className="vp-fill"
            type="submit"
            disabled={sending}
            aria-busy={sending}
            style={primary(!sending)}
          >
            Reset password
          </button>
          <div
            style={{
              fontFamily: fonts.text,
              fontSize: '13px',
              color: colors.mutedInk,
              textAlign: 'center',
            }}
          >
            The code expires in 60 minutes.
          </div>
        </div>
      </form>
    </AuthFrame>
  );
}

/**
 * D29. What the field says before sending, when the code cannot be a real one.
 * A code cut short was the H5 run's own mistake: the old placeholder drew
 * three groups of a seven-group code.
 */
function refusalForCode(fault: ResetCodeFault): Refusal {
  switch (fault.kind) {
    case 'empty':
      return { message: 'Enter the code from the email.' };
    case 'short':
      return {
        message: `That code is too short: ${fault.length} of ${RESET_CODE_LENGTH} characters.`,
        hint: 'The code in the email has seven groups, the last of three. Copy all of it.',
      };
    case 'long':
      return {
        message: `That code is too long: ${fault.length} characters, not ${RESET_CODE_LENGTH}.`,
        hint: 'Check that nothing was copied with it.',
      };
    case 'character':
      return {
        message: `That code has a character no code contains: ${fault.character}`,
        hint: 'Copy the code from the email again.',
      };
    case 'typo':
      return {
        message: 'That code has a typo.',
        hint: 'Check each character against the email.',
      };
  }
}

/** D28. The API answers one code for a wrong code and a spent or expired one. */
const wrongResetCode: Refusal = {
  message: 'That code is not right, or it has expired.',
  hint: 'Check it, or go back and ask for a new one.',
};

/**
 * While a stored session is spent on load: the mobile lockup, which Splash and
 * its failure screen share, in place of the nothing F4 drew. D20.
 */
export function Restoring() {
  return (
    <div aria-busy="true" style={{ ...ground, alignItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28 }}>
        <Lockup pulse />
        <p role="status" style={{ ...type.meta, color: colors.mutedInk, margin: 0 }}>
          Restoring your session
        </p>
      </div>
    </div>
  );
}

/**
 * A stored session the API gave no answer about, D6: the token is kept. Mobile's
 * `ConfigFailureScreen` with its catch-all copy, since the session cannot tell a
 * network failure from a 500 here, and Try again spends the token again.
 */
export function Unreachable() {
  const session = useSession();
  return (
    <div style={{ ...ground, alignItems: 'center' }}>
      <div
        style={{
          width: 390,
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '0 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 32,
        }}
      >
        <Lockup />
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontFamily: fonts.text,
              fontSize: '16px',
              lineHeight: 1.5,
              color: colors.navy,
            }}
          >
            Veriplus cannot start right now.
          </div>
          <div
            style={{
              fontFamily: fonts.text,
              fontSize: '13px',
              lineHeight: 1.5,
              color: colors.mutedInk,
            }}
          >
            Something went wrong at our end. Try again in a moment.
          </div>
        </div>
        <button
          className="vp-fill"
          type="button"
          onClick={() => void session.restore()}
          style={{ ...primary(true), alignSelf: 'stretch' }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function Lockup({ pulse = false }: { pulse?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <BrandMark size={120} decorative pulse={pulse} />
      <Wordmark size={32} color={colors.navy} />
    </div>
  );
}

/** OtpEntry's back button, which every step after the first shares. */
export function BackButton({ onBack, disabled }: { onBack: () => void; disabled?: boolean }) {
  return (
    <button
      className="vp-outline"
      type="button"
      onClick={onBack}
      disabled={disabled}
      aria-label="Back"
      style={back}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M14 5 L7 12 L14 19"
          stroke={colors.navy}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

/** OtpEntry's heading and its line under it, at OtpEntry's sizes. */
export function StepHeading({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h1 style={{ ...wordmark, fontSize: '28px', letterSpacing: '-0.4px', margin: 0 }}>{title}</h1>
      <p
        style={{
          fontFamily: fonts.text,
          fontSize: '15px',
          lineHeight: 1.5,
          color: colors.mutedInk,
          margin: 0,
        }}
      >
        {children}
      </p>
    </div>
  );
}

/** Welcome's password box: the field and the eye that reveals it. */
export function PasswordBox({
  id,
  value,
  onChange,
  autoComplete,
  disabled,
  refusal,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  disabled: boolean;
  refusal: Refusal | undefined;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div
      className="vp-box"
      style={{
        ...input,
        borderColor: borderFor(refusal),
        display: 'flex',
        alignItems: 'center',
        padding: '0 0 0 16px',
        overflow: 'hidden',
      }}
    >
      <input
        id={id}
        type={revealed ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-invalid={refusal !== undefined}
        aria-describedby={refusal !== undefined ? `${id}-refusal` : undefined}
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          padding: 0,
          font: 'inherit',
          color: 'inherit',
        }}
      />
      <button
        type="button"
        onClick={() => setRevealed((r) => !r)}
        disabled={disabled}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        style={reveal}
      >
        <EyeIcon slashed={revealed} />
      </button>
    </div>
  );
}

/**
 * A confirmation, D28. No source draws one at sign-in: the Rejection box's
 * shape in the genuine verdict's three drawn colours.
 */
export function Notice({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      style={{
        borderRadius: 12,
        background: verdict.genuine.bg,
        border: `1px solid ${verdict.genuine.border}`,
        padding: '12px 14px',
        fontFamily: fonts.text,
        fontSize: '12.5px',
        lineHeight: 1.5,
        color: verdict.genuine.fg,
      }}
    >
      {children}
    </div>
  );
}

export function FieldBox({
  id,
  label,
  refusal,
  children,
}: {
  id: string;
  label: string;
  refusal: Refusal | undefined;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        htmlFor={id}
        style={{
          fontFamily: fonts.text,
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: colors.mutedInk,
        }}
      >
        {label}
      </label>
      {children}
      {refusal !== undefined ? (
        <div
          id={`${id}-refusal`}
          role="alert"
          style={{
            fontFamily: fonts.text,
            fontSize: '13px',
            lineHeight: 1.5,
            color: verdict.unknown.fg,
          }}
        >
          <RefusalWords refusal={refusal} />
        </div>
      ) : null}
    </div>
  );
}

export function RefusalWords({ refusal }: { refusal: Refusal }) {
  return (
    <>
      {refusal.message}
      {refusal.hint !== undefined ? (
        <span style={{ color: colors.mutedInk }}> {refusal.hint}</span>
      ) : null}
    </>
  );
}

/** Mobile's `reveal` and `conceal`, `Icon.tsx`: the eye, and the same eye with a slash. */
function EyeIcon({ slashed }: { slashed: boolean }) {
  const stroke = { stroke: colors.mutedInk, strokeWidth: 2, strokeLinecap: 'round' as const };
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M2 12 Q12 4 22 12 Q12 20 2 12 Z" {...stroke} strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3.2" stroke={colors.mutedInk} strokeWidth={2} />
      {slashed ? <path d="M3 3 L21 21" {...stroke} /> : null}
    </svg>
  );
}

const ground: CSSProperties = {
  minHeight: '100vh',
  background: colors.ground,
  display: 'flex',
  justifyContent: 'center',
};

const wordmark: CSSProperties = {
  fontFamily: fonts.display,
  fontWeight: 800,
  letterSpacing: '-0.5px',
  color: colors.navy,
};

const reveal: CSSProperties = {
  width: 44,
  height: 44,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};

const back: CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 22,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  cursor: 'pointer',
};

const cell: CSSProperties = {
  boxSizing: 'border-box',
  width: 46,
  height: 58,
  borderRadius: 14,
  background: colors.card,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: '24px',
  color: colors.navy,
};

/** Over the cells and invisible: it takes the focus, the keys and the paste. */
const hiddenInput: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  border: 'none',
  padding: 0,
  cursor: 'text',
};

/** OtpEntry's Resend link, at the field labels' 13px. */
const linkButton: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontFamily: fonts.text,
  fontSize: '13px',
  fontWeight: 600,
  color: colors.brand,
  cursor: 'pointer',
};

const resendButton: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  fontWeight: 600,
  color: colors.brand,
  cursor: 'pointer',
};
